import { useMemo, useState } from 'react';
import { LuCircleCheck } from 'react-icons/lu';
import { Spinner } from 'reactstrap';

import { servicesSummary } from 'src/features/appointments/calendarUtils';
import { useCalendar } from 'src/features/appointments/useAppointments';
import type { Appointment } from 'src/features/appointments/types';
import { useBusiness } from 'src/features/auth/useAuth';
import ActionSheet, { ActionSheetItem } from 'src/shared/components/ui/ActionSheet';
import EmptyState from 'src/shared/components/ui/EmptyState';
import {
  addDays,
  formatDayShort,
  formatTime,
  todayKey,
  zonedDateKey,
} from 'src/shared/utils/datetime';
import { formatMoney } from 'src/shared/utils/money';
import PaymentFormModal from './PaymentFormModal';
import { isChargeable } from './paymentUtils';

/** How far back an unpaid appointment still shows up here. */
const LOOKBACK_DAYS = 7;

interface ChargeSheetProps {
  toggle: () => void;
}

/**
 * "Cobrar una cita" from anywhere: the appointments still owing money, today's
 * and the last week's, one tap from the payment form.
 *
 * Payments are always recorded against an appointment (that is the API), so
 * this is a way to *find* one quickly, not a second way to take money.
 */
export default function ChargeSheet({ toggle }: ChargeSheetProps) {
  const { data: business } = useBusiness();
  const timeZone = business?.timezone;
  const today = todayKey(timeZone);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const calendar = useCalendar({ from: addDays(today, -LOOKBACK_DAYS), to: today });

  const owing = useMemo(
    () =>
      (calendar.data ?? [])
        .filter((appointment) => isChargeable(appointment) && appointment.balanceDue > 0)
        // Most recent first: the client standing at the counter is today's.
        .sort((a, b) => b.startsAt.localeCompare(a.startsAt)),
    [calendar.data]
  );

  // Read from the refetched rows, never a stale copy: the form sends the
  // balance it shows.
  const selected: Appointment | null = owing.find((a) => a.id === selectedId) ?? null;

  const dayLabel = (appointment: Appointment) => {
    const day = zonedDateKey(appointment.startsAt, timeZone);
    return day === today ? 'Hoy' : formatDayShort(day);
  };

  return (
    <>
      <ActionSheet
        isOpen={selected === null}
        toggle={toggle}
        title="Cobrar una cita"
        subtitle="Citas de hoy y de los últimos 7 días con saldo pendiente"
      >
        {calendar.isPending ? (
          <div className="text-center py-4">
            <Spinner size="sm" />
          </div>
        ) : owing.length === 0 ? (
          <EmptyState icon={LuCircleCheck} title="Todo cobrado">
            No hay citas con saldo pendiente en los últimos 7 días.
          </EmptyState>
        ) : (
          owing.map((appointment) => (
            <ActionSheetItem
              key={appointment.id}
              label={appointment.clientName ?? 'Clienta'}
              hint={`${dayLabel(appointment)} · ${formatTime(appointment.startsAt, timeZone)} · ${servicesSummary(appointment)}`}
              trailing={formatMoney(appointment.balanceDue, business?.currency)}
              onClick={() => setSelectedId(appointment.id)}
            />
          ))
        )}
      </ActionSheet>

      {selected && (
        <PaymentFormModal
          isOpen
          toggle={() => setSelectedId(null)}
          appointment={selected}
          currency={business?.currency}
          timeZone={timeZone}
        />
      )}
    </>
  );
}
